import {useState, useRef, ReactElement} from 'react';
// eslint-disable-next-line import/no-named-as-default
import ReactCrop, {Crop, PixelCrop} from 'react-image-crop';

import {Button, Modal} from '@mui/material';
import 'react-image-crop/dist/ReactCrop.css';

type ImageCropModalProps = {
  image: string;
  onSave: (croppedImage: string) => void;
  onClose: () => void;
  saveButtonText?: string;
  saveButtonStartIcon?: ReactElement;
  cancelButtonText?: string;
};

function ImageCropModal({
  image,
  onSave,
  onClose,
  saveButtonText,
  saveButtonStartIcon,
  cancelButtonText,
}: ImageCropModalProps) {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<string> => {
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement('canvas');
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return Promise.reject(new Error('Could not get canvas context'));
    }

    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      }, 'image/png');
    });
  };

  const handleCropClick = async () => {
    if (completedCrop && imageRef.current) {
      try {
        const croppedImageUrl = await getCroppedImg(imageRef.current, completedCrop);
        onSave(croppedImageUrl);
      } catch (error) {
        console.error('Crop failed:', error);
      }
    }
  };

  return (
    <Modal open={true}>
      <div className="bg-background-default rounded-xl h-fit w-[720px] max-h-[85vh] overflow-auto absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none p-4">
        <p className="text-center text-sm text-gray-300 font-medium mb-2">HINT: Crop the image to the desired area</p>
        <ReactCrop
          crop={crop}
          onChange={(newCrop) => setCrop(newCrop)}
          aspect={1200 / 630}
          onComplete={(c) => setCompletedCrop(c)}
        >
          <img ref={imageRef} src={image} alt="Crop preview" className="max-w-full" />
        </ReactCrop>
        <div className="mt-4 flex justify-center gap-4">
          <Button
            onClick={() => handleCropClick()}
            variant="contained"
            disabled={!completedCrop}
            startIcon={saveButtonStartIcon ?? null}
            color="primary"
          >
            {saveButtonText ?? 'Crop'}
          </Button>
          <Button onClick={() => onClose()} variant="contained" color="info">
            {cancelButtonText ?? 'Cancel'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ImageCropModal;

/* de rezolvat:
- selectarea automata a 



*/
